/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect, useRef } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { Button } from "@mui/material";

import GridSectionHeader from "../../../widgets/GridSectionHeader";
import GridDropdown from "../../../widgets/GridDropdown";
import GridTextfield from "../../../widgets/GridTextfield";
import GridCheckbox from "../../../widgets/GridCheckbox";

/*============================================================
  PROPRIETARY - UTILS
============================================================*/
import { reNumbers } from "../../../../utils/fieldValidator.js";
import { createPayload, createReduxAction } from "../../../../utils/modelHelper.js";
import { validateCurrencyField3 } from "../../../../utils/fieldValidator.js";
import { ConstructionOutlined } from "@mui/icons-material";

const needsMap = new Map();
needsMap.set("familyAndAssets", "Family And Assets");
needsMap.set("disability", "Disability");
needsMap.set("criticalIlness", "Critical Illness");
needsMap.set("longTermCare", "Long Term Care");
needsMap.set("hospitalization", "Hospitalization");
needsMap.set("personalAccident", "Personal Accident");
needsMap.set("pregnancy", "Pregnancy");
needsMap.set("dependantNeeds", "Dependant Needs");
needsMap.set("retirementPlanning", "Retirement Planning");
needsMap.set("childrenEducation", "Children Education");
needsMap.set("otherPurpose", "Other Purpose");

var investmentPlatformMap = new Map();

const buyOrSwitchData = [
  { key: "BUY", value: "Buy" },
  { key: "SWITCH", value: "Switch" },
];

const wrapOrUnwrapData = [
  { key: "WRAP", value: "Wrap Account" },
  { key: "NON-WRAP", value: "Non-Wrap Account" },
];

const paymentFrequencyData = [
  { key: "Annual", value: "Annual" },
  { key: "Half-Yearly", value: "Half-Yearly" },
  { key: "Quarterly", value: "Quarterly" },
  { key: "Monthly", value: "Monthly" },
  { key: "Single", value: "Single" },
];

var investmentRecommendation = {}; //Single recommended product

export default function ProductInvestment() {
  const applicantCount = useSelector((state) => state.app.applicantCount);
  const currApplicant = useSelector((state) => state.app.currApplicant);
  const applicants = useSelector((state) => state.personalInformation);
  const personalInfo = useSelector((state) => state.personalInformation[currApplicant]);

  const protection = useSelector((state) => state.objectives.protection);
  const wealthaccumulation = useSelector((state) => state.objectives.wealthaccumulation);
  const investmentRisk = useSelector((state) => state.investmentRisk);

  const [proposer, setProposer] = useState("");
  const [needs, setNeeds] = useState("");
  const [investmentPlatform, setInvestmentPlatform] = useState("");
  const [wrapOrUnwrap, setWrapOrUnwrap] = useState("");
  const [buyOrSwitch, setBuyOrSwitch] = useState("");
  const [totalInvestAmt, setTotalInvestAmt] = useState("");

  //Fund Category
  //Fund House
  const [fundName, setFundName] = useState("");
  const [paymentFrequency, setPaymentFrequency] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [invPlatformData, setInvPlatformData] = useState([]);
  const [invFundListData, setInvFundListData] = useState([]);

  var proposersData = [];
  var needsData = [];

  /**==============================
   * EFFECTS - INVESTMENT PLATFORM
   ==============================*/
  useEffect(() => {
    const fetchInvestmentPlatform = async () => {
      try {
        if (invPlatformData.length === 0) {
          const response = await fetch("https://fp-api.3nity.tech/investment_platform");

          if (!response.ok) throw new Error("Failed to fetch investment platform");
          const data = await response.json();

          const invPlatformList = data.investment_platform_list;
          invPlatformList.map((platform) => {
            investmentPlatformMap.set(platform.id, platform.name);
            invPlatformData.push({ key: platform.id, value: platform.name });
          });

          setInvPlatformData(invPlatformData);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInvestmentPlatform();
  }, []);

  /**==============================
   * EFFECTS - INVESTMENT FUNDS LIST
   ==============================*/
  useEffect(() => {
    const fetchInvestmentFunds = async () => {
      try {
        if (invFundListData.length === 0) {
          //ASSUMPTION FUND HOUSE ID = 2
          const response = await fetch("https://fp-api.3nity.tech/fund/2");

          if (!response.ok) throw new Error("Failed to fetch fund list");
          const data = await response.json();

          const invFundList = data.fund_list;
          invFundList.map((fund) => {
            console.log("fund.name = " + fund.name);
            console.log("fund.isin = " + fund.isin);

            //investmentPlatformMap.set(platform.id, platform.name);
            invFundListData.push({ key: fund.id, value: fund.name });
          });

          setInvPlatformData(invFundListData);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInvestmentFunds();
  }, []);

  initComponent();

  function initComponent() {
    initProposer();
    initNeeds();
  }

  function initProposer() {
    proposersData = [];

    //INITIALIZE PROPOSER
    proposersData.push({ key: personalInfo.fullName, value: personalInfo.fullName });
  }

  function initNeeds() {
    //INITIALIZE NEEDS - PROTECTION
    needsData.splice(0, needsData.length);

    // for (let key in protection) {
    //   let priority = protection[key].priority;

    //   if (priority === "MEDIUM" || priority === "HIGH") {
    //     needsData.push({ key: `${key}`, value: "[" + priority + "]: " + needsMap.get(`${key}`) });
    //   }
    // }

    for (let key in wealthaccumulation) {
      let priority = wealthaccumulation[key].priority;
      if (priority === "MEDIUM" || priority === "HIGH")
        needsData.push({ key: `${key}`, value: "[" + priority + "]: " + needsMap.get(`${key}`) });
    }
  }

  /**==============================
   * ACTION HANDLER
   ==============================*/
  function handleChange(e, object) {
    let id = "";

    let actionType = "";
    let updateAction = null;
    let payload = null;

    if (e instanceof PointerEvent) {
      id = object.props.id;

      if (id === "proposer") {
        investmentRecommendation.proposer = e.target.value;
        setProposer(e.target.value);
      }

      if (id === "needs") {
        investmentRecommendation.needs = e.target.value;
        setNeeds(e.target.value);
      }

      if (id === "investmentPlatform") {
        investmentRecommendation.investmentPlatform = e.target.value;
        setInvestmentPlatform(e.target.value);
      }

      if (id === "wrapOrUnwrap") {
        investmentRecommendation.wrapOrUnwrap = e.target.value;
        setWrapOrUnwrap(e.target.value);
      }

      if (id === "buyOrSwitch") {
        investmentRecommendation.buyOrSwitch = e.target.value;
        setBuyOrSwitch(e.target.value);
      }

      if (id === "fundName") {
        investmentRecommendation.fundName = e.target.value;
        setFundName(e.target.value);

        console.log("fundName");
        console.log(e.target.value);
      }

      if (id === "paymentFrequency") {
        investmentRecommendation.paymentFrequency = e.target.value;

        setPaymentFrequency(e.target.value);
        //actionType = "UPDATE_PAYMENT_FREQUENCY";
      }
    } else {
      id = e.target.id;

      let input_data = {
        event: e,
        fieldName: "",
        fieldVariable: "",
        isMandatory: false,
      };

      if (id === "totalInvestAmt") {
        input_data.fieldName = "Total Investment Amount";
        input_data.fieldVariable = totalInvestAmt;

        const result = validateCurrencyField3(input_data);
        if (result.errorMessage === "") {
          setTotalInvestAmt(result.value);
          investmentRecommendation.totalInvestmentAmt = result.value;
        }
      }
    }
  }

  return (
    <>
      <Grid container spacing={2}>
        <GridSectionHeader sectionName="Product Recommendation - Investment" />

        {/* Proposer */}
        <GridDropdown
          id="proposer"
          key="proposer"
          columnSize="6"
          label="Proposer"
          currState={proposer}
          onChange={handleChange}
          data={proposersData}
        />

        {/* Clients's Risk Profile */}
        <GridTextfield
          id="clientRiskProfile"
          key="clientRiskProfile"
          columnSize="6"
          label="Client Risk Profile"
          currState={investmentRisk.riskType}
          disabled={true}
        />

        {/* Needs */}
        <GridDropdown
          id="needs"
          key="needs"
          columnSize="12"
          label="Needs"
          currState={needs}
          onChange={handleChange}
          data={needsData}
        />

        {/* INVESTMENT PLATFORM */}
        <GridDropdown
          id="investmentPlatform"
          key="investmentPlatform"
          columnSize="6"
          label="Investment Platform"
          currState={investmentPlatform}
          onChange={handleChange}
          data={invPlatformData}
        />

        {/* WRAP / NON-WRAP ACCOUNT */}
        <GridDropdown
          id="wrapOrUnwrap"
          key="wrapOrUnwrap"
          columnSize="6"
          label="Wrap / Non-Wrap Account"
          currState={wrapOrUnwrap}
          onChange={handleChange}
          data={wrapOrUnwrapData}
        />

        {/* BUY / SWITCH */}
        <GridDropdown
          id="buyOrSwitch"
          key="buyOrSwitch"
          columnSize="6"
          label="But / Switch"
          currState={buyOrSwitch}
          onChange={handleChange}
          data={buyOrSwitchData}
        />

        {/* TOTAL INVESTMENT AMOUNT */}
        <GridTextfield
          id="totalInvestAmt"
          key="totalInvestAmt"
          columnSize="6"
          label="Total Investment Amount"
          currState={totalInvestAmt}
          onChange={handleChange}
        />

        {/*==============================
        FUND DETAILS
       ==============================*/}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom sx={{ backgroundColor: "lightgray" }}>
            Fund 1
          </Typography>
        </Grid>

        {/* FUND CATEGORY */}
        <GridDropdown
          id="fundCategory"
          key="fundCategory"
          columnSize="6"
          label="Fund Category"
          defaultValue=""
          value=""
          // currState={fundCategory}
          // onChange={handleFundCategoryChange}
          // data={fundCategoryData}
        />

        {/* FUND HOUSE */}
        <GridTextfield
          id="fundHouse"
          key="fundHouse"
          columnSize="6"
          label="Fund House"
          // currState={fundHouse}
          // onChange={handleFundHouseChange}
        />

        {/* FUND NAME */}
        <GridDropdown
          id="fundName"
          key="fundName"
          columnSize="6"
          label="Fund Name"
          defaultValue=""
          value=""
          // currState={fundName}
          // onChange={handleFundNameChange}

          // currState={investmentFundName}
          onChange={handleChange}
          data={invFundListData}
        />

        {/* FUND ISIN */}
        <GridDropdown
          id="fundISIN"
          key="fundISIN"
          columnSize="6"
          label="Fund ISIN"
          defaultValue=""
          value=""
          // currState={fundISIN}
          // onChange={handleFundISINChange}
          // data={emptydata}
        />

        {/* FUND CODE */}
        <GridDropdown
          id="fundCode"
          key="fundCode"
          columnSize="6"
          label="Fund Code"
          defaultValue=""
          value=""
          // currState={fundCode}
          // onChange={handleFundCodeChange}
          // data={emptydata}
        />

        {/* ASSET CLASS */}
        <GridDropdown
          id="assetClass"
          key="assetClass"
          columnSize="6"
          label="Asset Class"
          defaultValue=""
          value=""
          // currState={assetClass}
          // onChange={handleAssetClassChange}
          // data={emptydata}
        />

        {/* GEOGRAPHICAL FOCUS */}
        <GridDropdown
          id="geoFocus"
          key="geoFocus"
          columnSize="6"
          label="Geographical Focus"
          defaultValue=""
          value=""
          // currState={geoFocus}
          // onChange={handleGeoFocusChange}
          // data={emptydata}
        />

        {/* RISK RATING */}
        <GridTextfield
          id="riskRating"
          key="riskRating"
          columnSize="6"
          label="Risk Rating"
          // currState={riskRating}
          // onChange={handleRiskRatingChange}
        />

        {/* RISK CLASSIFICATION */}
        <GridDropdown
          id="riskClass"
          key="riskClass"
          columnSize="6"
          label="Risk Classification"
          defaultValue=""
          value=""
          // currState={riskClass}
          // onChange={handleRiskClassChange}
          // data={emptydata}
        />

        {/* FREQUENCY */}
        <GridDropdown
          id="frequency"
          key="frequency"
          columnSize="6"
          label="Frequency"
          defaultValue=""
          value=""
          currState={paymentFrequency}
          onChange={handleChange}
          data={paymentFrequencyData}
        />

        {/* CURRENCY */}
        <GridDropdown
          id="currency"
          key="currency"
          columnSize="6"
          label="Currency"
          defaultValue=""
          value=""
          // currState={currency}
          // onChange={handleCurrencyChange}
          // data={emptydata}
        />

        {/* ALLOCATION */}
        <GridTextfield
          id="allocation"
          key="riskRating"
          columnSize="6"
          label="Allocation"
          // currState={riskRating}
          // onChange={handleRiskRatingChange}
        />

        {/* INVESTMENT AMOUNT */}
        <GridTextfield
          id="investmentAmount"
          key="investmentAmount"
          columnSize="6"
          label="Investment Amount"
          // currState={investmentAmount}
          // onChange={handleInvestmentAmountChange}
        />

        {/*==============================
         COMMANDS
        ==============================*/}
        <Grid size={12}>
          <Stack spacing={2} direction="row" justifyContent="center">
            <Button
              variant="contained"
              sx={{ width: "140px" }}
              disabled={false}
              // onClick={handleCancel}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              sx={{ width: "140px" }}
              disabled={false}
              // onClick={handleAddProduct}
            >
              Add Product
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}
